import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../api/admin';
import { useUiStore } from '../../store/uiStore';
import { Spinner } from '../../components/Spinner';
import type { PaymentRequest } from '../../types';

type StatusFilter = 'pending' | 'approved' | 'rejected';

const STATUSES: StatusFilter[] = ['pending', 'approved', 'rejected'];

export function AdminPayments() {
  const pushToast = useUiStore((s) => s.pushToast);

  const [status, setStatus] = useState<StatusFilter>('pending');
  const [requests, setRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async (s: StatusFilter) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await adminApi.listPaymentRequests(s);
      setRequests(res.items);
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : 'Failed to load payment requests.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status);
  }, [load, status]);

  async function handleReview(
    req: PaymentRequest,
    decision: 'approve' | 'reject',
  ) {
    let reason: string | undefined;
    if (decision === 'reject') {
      const input = window.prompt('Reason for rejecting this request:');
      if (input === null) return;
      if (!input.trim()) {
        pushToast('error', 'A reason is required to reject.');
        return;
      }
      reason = input.trim();
    }
    setActingId(req.id);
    try {
      await adminApi.reviewPayment(req.id, decision, reason);
      pushToast(
        'success',
        `Payment ${decision === 'approve' ? 'approved' : 'rejected'}.`,
      );
      await load(status);
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Review failed.',
      );
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-100">Payment requests</h1>

      <div className="flex gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={s === status ? 'btn-gold capitalize' : 'btn-ghost capitalize'}
            onClick={() => setStatus(s)}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : errorMsg ? (
        <p className="card text-sm text-rose-400">{errorMsg}</p>
      ) : requests.length === 0 ? (
        <p className="card text-sm text-slate-400">
          No {status} payment requests.
        </p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-slate-400">
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Tokens</th>
                <th className="py-2 pr-3">Proof</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-b border-white/5 align-middle">
                  <td className="py-2 pr-3 font-mono text-xs text-slate-300">
                    {req.uid}
                  </td>
                  <td className="py-2 pr-3 text-gold">
                    {req.tokenAmount.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    <a
                      href={req.proofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-gold underline hover:text-gold-dark"
                    >
                      View proof
                    </a>
                  </td>
                  <td className="py-2 pr-3 capitalize text-slate-300">
                    {req.status}
                  </td>
                  <td className="py-2 pr-3 text-slate-400">
                    {new Date(req.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    {req.status === 'pending' ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="btn-gold px-2 py-1 text-xs"
                          onClick={() => handleReview(req, 'approve')}
                          disabled={actingId === req.id}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn-ghost px-2 py-1 text-xs"
                          onClick={() => handleReview(req, 'reject')}
                          disabled={actingId === req.id}
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
